// Shape definitions for the two-hand drawing app.
//
// Each shape is a closed (or open) path of checkpoints arranged around a
// 200x200 logical coordinate space. The tracer must clear the checkpoints in
// sequential order, just like the handwriting tracer.

export const SHAPE_IDS = [
  'square',
  'circle',
  'open_circle',
  'triangle',
  'trapezoid',
  'hexagon',
  'letter_z',
]

export const SHAPE_LABELS = {
  square: 'Square',
  circle: 'Circle',
  open_circle: 'Open Circle',
  triangle: 'Triangle',
  trapezoid: 'Trapezoid',
  hexagon: 'Hexagon',
  letter_z: 'Letter Z',
}

// Shapes whose path does not loop back to its starting point.
export const OPEN_SHAPE_IDS = ['open_circle', 'letter_z']

// Canvas logical size — shapes are designed to live inside 200x200 then
// centered/scaled by the canvas renderer.
export const SHAPE_BOX = 200

// Centre and radius used when generating circular checkpoints.
const CENTRE = { x: 100, y: 100 }
const RADIUS = 80

function pointOnCircle(angle) {
  return {
    x: CENTRE.x + Math.cos(angle) * RADIUS,
    y: CENTRE.y + Math.sin(angle) * RADIUS,
  }
}

function buildSquare() {
  // Start at top-left, walk clockwise.
  const min = CENTRE.x - RADIUS
  const max = CENTRE.x + RADIUS
  const mid = CENTRE.x
  return [
    { x: min, y: min },
    { x: mid, y: min },
    { x: max, y: min },
    { x: max, y: mid },
    { x: max, y: max },
    { x: mid, y: max },
    { x: min, y: max },
    { x: min, y: mid },
    { x: min, y: min }, // close back to start
  ]
}

function buildCircle() {
  // 12 evenly spaced checkpoints around the circumference, closed.
  const segments = 12
  const points = []
  for (let i = 0; i <= segments; i++) {
    // Start at the top of the circle and walk clockwise.
    const angle = -Math.PI / 2 + (i / segments) * Math.PI * 2
    points.push(pointOnCircle(angle))
  }
  return points
}

function buildOpenCircle() {
  // 270° arc starting at the right side and walking clockwise back to the top.
  // The "opening" sits between the top and the right side of the circle.
  const segments = 10
  const startAngle = 0 // 3 o'clock
  const sweep = (3 / 4) * Math.PI * 2 // 270 degrees
  const points = []
  for (let i = 0; i <= segments; i++) {
    const angle = startAngle + (i / segments) * sweep
    points.push(pointOnCircle(angle))
  }
  return points
}

function buildTriangle() {
  // Equilateral-ish triangle inside the same circle, walked clockwise from
  // the top vertex back to itself.
  const top = pointOnCircle(-Math.PI / 2)
  const bottomRight = pointOnCircle(-Math.PI / 2 + (2 * Math.PI) / 3)
  const bottomLeft = pointOnCircle(-Math.PI / 2 + (4 * Math.PI) / 3)
  // Insert midpoints to give the tracer more checkpoints to clear.
  const midRight = midpoint(top, bottomRight)
  const midBottom = midpoint(bottomRight, bottomLeft)
  const midLeft = midpoint(bottomLeft, top)
  return [top, midRight, bottomRight, midBottom, bottomLeft, midLeft, top]
}

function midpoint(a, b) {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
}

function buildTrapezoid() {
  // Isosceles trapezoid — narrower top, wider bottom — walked clockwise.
  const topLeft = { x: 60, y: 30 }
  const topRight = { x: 140, y: 30 }
  const bottomRight = { x: 180, y: 170 }
  const bottomLeft = { x: 20, y: 170 }
  return [
    topLeft,
    midpoint(topLeft, topRight),
    topRight,
    midpoint(topRight, bottomRight),
    bottomRight,
    midpoint(bottomRight, bottomLeft),
    bottomLeft,
    midpoint(bottomLeft, topLeft),
    topLeft,
  ]
}

function buildHexagon() {
  // Regular hexagon inscribed in the shape circle, starting at the top vertex
  // and walking clockwise.
  const sides = 6
  const points = []
  for (let i = 0; i <= sides; i++) {
    const angle = -Math.PI / 2 + (i / sides) * Math.PI * 2
    points.push(pointOnCircle(angle))
  }
  return points
}

function buildLetterZ() {
  // Letter Z — open path (top stroke, diagonal, bottom stroke) traced left to
  // right, top to bottom. Extra midpoints keep consecutive checkpoints close
  // enough to be reachable without huge gaps.
  const topLeft = { x: 30, y: 30 }
  const topRight = { x: 170, y: 30 }
  const bottomLeft = { x: 30, y: 170 }
  const bottomRight = { x: 170, y: 170 }
  const diagonalMid = midpoint(topRight, bottomLeft)
  return [
    topLeft,
    midpoint(topLeft, topRight),
    topRight,
    midpoint(topRight, diagonalMid),
    diagonalMid,
    midpoint(diagonalMid, bottomLeft),
    bottomLeft,
    midpoint(bottomLeft, bottomRight),
    bottomRight,
  ]
}

const BUILDERS = {
  square: buildSquare,
  circle: buildCircle,
  open_circle: buildOpenCircle,
  triangle: buildTriangle,
  trapezoid: buildTrapezoid,
  hexagon: buildHexagon,
  letter_z: buildLetterZ,
}

export function getShapePath(shapeId) {
  const builder = BUILDERS[shapeId]
  if (!builder) return []
  return builder()
}

export function isValidShapeId(shapeId) {
  return SHAPE_IDS.includes(shapeId)
}

// Tiny deterministic PRNG (Mulberry32) — same seed always returns the same
// sequence so server and client agree on the shapes shown for a given pair.
function mulberry32(seed) {
  let a = seed >>> 0
  return function () {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/**
 * Derive the left and right shape ids for a given (sessionId, counter) pair.
 * Deterministic: same inputs always produce the same shapes.
 *
 * Constraint: left !== right so the kid always traces two different shapes.
 */
export function deriveShapesForRound(sessionId, counter) {
  const numericSession = Number(sessionId) || 0
  const numericCounter = Number(counter) || 0
  // Combine into a single seed; multiplying by primes keeps adjacent rounds
  // visually different even when the session id is small.
  const seed = numericSession * 1000003 + numericCounter * 2654435761
  const rng = mulberry32(seed)
  const left = SHAPE_IDS[Math.floor(rng() * SHAPE_IDS.length)]
  let right = SHAPE_IDS[Math.floor(rng() * SHAPE_IDS.length)]
  if (right === left) {
    right = SHAPE_IDS[(SHAPE_IDS.indexOf(left) + 1) % SHAPE_IDS.length]
  }
  return { left, right }
}
